/**
 * IssuesBoard — the Work issues kanban as an ADAPTER over the canonical `Board`
 * (UI/UX Patterns PRD Issue 11, follow-up to the CRM reference migration).
 *
 * The faithful, drag-preserving replacement for the legacy `KanbanBoard` fork:
 * SAME external contract (`scope` / `byStatus` / `actions`), but the kanban
 * engine — dnd-kit drag, DragOverlay, optimistic placement with revert,
 * horizontal column-scroll + edge-fade — now lives ONCE in `Board`. Everything
 * issue-specific rides on adapter props:
 *   • columns     ← the fixed TaskStatus lifecycle (backlog → done, minus
 *                    cancelled), colored by `TASK_STATUS_INTENT` and badged
 *                    with the column count.
 *   • renderCard  ← the task tile led by a `StatusRing` (Issue 6 — the one
 *                    canonical at-a-glance status glyph), wrapped in the shared
 *                    `TaskContextMenu` so right-click "Move to …" still works.
 *   • onMove      ← the existing scope-aware status change: section scope keeps
 *                    ordering via `moveTaskToStatus`, project/workspace scope
 *                    uses `setStatus` — identical routing to KanbanBoard.
 *   • onOpen      ← deep-link the task peek via `?task=<id>` (TaskCard's click).
 *
 * No new board is forked here; this file only shapes issue data for `Board`.
 * The old `kanban-board.tsx` stays in place (additive migration).
 */

import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../../router";
import { cn } from "../../utils";
import { formatDate } from "../../format";
import { sectionColor, INTENT_CLASSES, type Intent } from "../../tokens";
import { TASK_STATUS_INTENT } from "../../tokens/status";
import {
  PRIORITY_LABEL,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_ORDER,
  type ScopedTask,
  type TaskStatus,
  type ViewScope,
} from "../../types/projects";
import { Board, type BoardColumn } from "../board";
import { StatusRing } from "../status-ring";
import { TaskContextMenu } from "./task-context-menu";

/** External contract — byte-identical to KanbanBoard's, so call sites only swap the name. */
export interface IssuesBoardActions {
  moveTaskToStatus: (
    taskScope: { projectSlug: string; sectionSlug: string },
    taskId: number,
    status: TaskStatus,
    beforeId: number | null,
    afterId: number | null,
  ) => Promise<unknown>;
  setStatus: (
    taskScope: { projectSlug: string; sectionSlug: string },
    taskId: number,
    status: TaskStatus,
  ) => Promise<unknown>;
}

/**
 * A non-task attention card for the board's Open column (Matt 07-16 — the fleet's
 * "Awaiting you" work, mapped to nearest status = Open). Serializable (no
 * functions): IssuesBoard builds the open handler from `taskId`/`href`.
 */
export interface BoardExtra {
  id: string;
  title: string;
  why?: string;
  /** Severity → the card's leading dot. */
  intent?: Intent;
  /** Opens the task peek in place when set (also the dedup key). */
  taskId?: number;
  /** Else navigates here. */
  href?: string;
}

export interface IssuesBoardProps {
  scope: ViewScope;
  byStatus: Record<TaskStatus, ScopedTask[]>;
  actions: IssuesBoardActions;
  /** Non-task work folded into the Open column — escalations, launch gates,
   *  stuck-run health, mentions. Read-only cards; deduped against tasks already
   *  on the board (a task-bound alert is redundant when its task is columned). */
  extras?: BoardExtra[];
}

/** Board item — a task tile OR a non-task attention card (Open column only). */
type BoardItem = { key: string; task?: ScopedTask; extra?: BoardExtra };

// backlog · todo · in_progress · review · done (cancelled is off-board — same as
// KanbanBoard's VISIBLE_STATUSES). These are the fixed lifecycle columns.
const VISIBLE_STATUSES: TaskStatus[] = STATUS_ORDER.filter(
  (s) => s !== "cancelled",
);

/**
 * The task tile — a leading StatusRing (Issue 6) + the title, then a single
 * quiet metadata footer (project/section dots + due date), matching TaskCard's
 * hierarchy. Board owns the draggable/clickable shell; this only draws the body,
 * wrapped in the right-click TaskContextMenu so "Move to …" is preserved.
 */
function TaskTile({
  task,
  scope,
  setStatus,
}: {
  task: ScopedTask;
  scope: ViewScope;
  setStatus: IssuesBoardActions["setStatus"];
}) {
  const due = task.due_date ? formatDate(task.due_date) : null;
  const flaggedPriority =
    task.priority === "high" || task.priority === "urgent";
  const showFooter = scope.kind !== "section" || Boolean(due);

  return (
    <TaskContextMenu task={task} actions={{ setStatus }}>
      <div className="flex flex-col gap-2 type-body">
        <div className="flex min-w-0 items-start gap-2">
          <StatusRing status={task.status} className="mt-0.5" />
          {flaggedPriority ? (
            <span
              aria-hidden="true"
              title={PRIORITY_LABEL[task.priority]}
              className={cn(
                "mt-1 size-1.5 shrink-0 rounded-full",
                PRIORITY_TONE[task.priority],
              )}
            />
          ) : null}
          <span className="line-clamp-2 min-w-0 flex-1 font-medium text-foreground">
            {task.title}
          </span>
        </div>

        {showFooter ? (
          <div className="flex min-w-0 items-center gap-1.5 pl-5 type-subtext text-muted-foreground">
            {scope.kind === "workspace" ? (
              <>
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      task.project_color ?? "var(--muted-foreground)",
                  }}
                />
                <span className="min-w-0 truncate">{task.project_name}</span>
                <span
                  aria-hidden="true"
                  title={task.section_name}
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: sectionColor(task.section_slug) }}
                />
              </>
            ) : scope.kind === "project" ? (
              <>
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: sectionColor(task.section_slug) }}
                />
                <span className="min-w-0 truncate">{task.section_name}</span>
              </>
            ) : null}
            {due ? (
              <span className="ml-auto shrink-0 pl-1 tabular-nums">{due}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </TaskContextMenu>
  );
}

/** A non-task attention card — a severity dot + title + the "why". Read-only:
 *  it can't change status, so it's never draggable (onMove rejects its key). */
function ExtraTile({ extra }: { extra: BoardExtra }) {
  return (
    <div className="flex flex-col gap-2 type-body">
      <div className="flex min-w-0 items-start gap-2">
        <span
          aria-hidden="true"
          className={cn("mt-1 size-2 shrink-0 rounded-full", INTENT_CLASSES[extra.intent ?? "info"].dot)}
        />
        <span className="line-clamp-2 min-w-0 flex-1 font-medium text-foreground">
          {extra.title}
        </span>
      </div>
      {extra.why ? (
        <div className="min-w-0 truncate pl-4 type-subtext text-muted-foreground">{extra.why}</div>
      ) : null}
    </div>
  );
}

const taskKey = (id: number) => `task-${id}`;

export function IssuesBoard({ scope, byStatus, actions, extras = [] }: IssuesBoardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  // Flat lookup so `onMove` can recover the moving task's own project/section
  // scope (needed by the shell's scope-aware status actions).
  const taskById = React.useMemo(() => {
    const map = new Map<number, ScopedTask>();
    for (const status of VISIBLE_STATUSES) {
      for (const t of byStatus[status]) map.set(t.id, t);
    }
    return map;
  }, [byStatus]);

  // Dedup: an alert bound to a task already on the board is redundant (the task
  // card is the single source). Only task-less attention survives → Open column.
  const shownExtras = React.useMemo(
    () => extras.filter((e) => e.taskId == null || !taskById.has(e.taskId)),
    [extras, taskById],
  );

  const columns = React.useMemo<BoardColumn<BoardItem>[]>(
    () =>
      VISIBLE_STATUSES.map((status) => {
        const taskItems: BoardItem[] = byStatus[status].map((t) => ({ key: taskKey(t.id), task: t }));
        // The non-task attention rides the Open (todo) column, leading it.
        const items =
          status === "todo"
            ? [...shownExtras.map((e) => ({ key: e.id, extra: e }) as BoardItem), ...taskItems]
            : taskItems;
        return {
          id: status,
          name: STATUS_LABEL[status],
          items,
          intent: TASK_STATUS_INTENT[status],
          badge: items.length,
          emptyLabel: "Drop issues here",
        };
      }),
    [byStatus, shownExtras],
  );

  const openTaskPeek = React.useCallback(
    (id: number) => {
      const next = new URLSearchParams(search.toString());
      next.set("task", String(id));
      router.replace(`${pathname}?${next.toString()}`);
    },
    [router, pathname, search],
  );

  const openItem = React.useCallback(
    (item: BoardItem) => {
      if (item.task) return openTaskPeek(item.task.id);
      const e = item.extra!;
      if (e.taskId != null) return openTaskPeek(e.taskId);
      if (e.href) router.push(e.href);
    },
    [openTaskPeek, router],
  );

  // Scope-aware persist, mapped onto Board's generic `(itemId, toColumnId)`.
  // Board applies the move optimistically and reverts if this promise rejects.
  // A non-task card can't change status, so its move REJECTS → Board reverts it.
  const onMove = React.useCallback(
    async (itemId: string | number, toColumnId: string | number) => {
      const task = taskById.get(Number(String(itemId).replace(/^task-/, "")));
      if (!task) throw new Error("not-movable"); // an extra card — revert the drag
      const toStatus = String(toColumnId) as TaskStatus;
      const taskScope = {
        projectSlug: task.project_slug,
        sectionSlug: task.section_slug,
      };
      if (scope.kind === "section") {
        await actions.moveTaskToStatus(taskScope, task.id, toStatus, null, null);
      } else {
        await actions.setStatus(taskScope, task.id, toStatus);
      }
    },
    [taskById, scope.kind, actions],
  );

  return (
    <Board<BoardItem>
      dndId="excellent-issues-board"
      columns={columns}
      getItemId={(i) => i.key}
      onMove={onMove}
      onOpen={openItem}
      renderCard={(i) =>
        i.task ? (
          <TaskTile task={i.task} scope={scope} setStatus={actions.setStatus} />
        ) : (
          <ExtraTile extra={i.extra!} />
        )
      }
    />
  );
}
